import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { toast, Bounce } from "react-toastify";
import {
    fetchPlanManagerClientConfig,
    fetchPlanManagerDefaultNotificationConfig,
    fetchPlanManagerHealthEdgeConfig,
    fetchPlanManagerHealthEdgeMedicalPlan,
    fetchPlanManagerHealthEdgePlansBatch,
    fetchPlanManagerMedicalPlan,
    fetchPlanManagerPlansBatch,
    fetchPlanManagerRefreshJobStatus,
    startPlanManagerHealthEdgeRefresh,
    startPlanManagerJavelinaRefresh,
} from "../../../utils/apiUtils.js";
import { useSelectedPlanStore } from "../../../store/selectedPlanStore.js";
import {
    PlanPlatformTab,
    PlanStatus,
    PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS,
    PLAN_MANAGER_QUERY_KEYS,
} from "../common/constants";
import {
    doesFileNameIncludePlanId,
    filterPlansBySearchCriteria,
    loadAllPlansGrids,
    validatePlanImportFileName,
    parsePlanImportJson,
    validatePlanImportIds,
    sanitizePlanExportPayload,
    buildPlanExportFileName,
    buildPlanDrillDownUrl,
    PLAN_IMPORT_ERROR_MESSAGES,
} from "../../../utils/planManager/planConfigurationUtils.js";

/**
 * User-facing copy for the two import confirmation modals, mirroring the legacy grammar.
 * @readonly
 */
export const IMPORT_MODAL_COPY = Object.freeze({
    OVERWRITE: Object.freeze({
        TITLE: "Are you sure you want to import?",
        BODY: "Importing will overwrite existing plan configuration.",
        CONFIRM: "Yes, import",
        CANCEL: "No, cancel",
    }),
    PLAN_ID_MISMATCH: Object.freeze({
        TITLE: "Plan IDs do not match. Are you sure you want to import?",
        BODY: "The imported plan ID does not match this plan. Importing anyway will overwrite the existing configuration.",
        CONFIRM: "Yes, import",
        CANCEL: "No, cancel",
    }),
});

/**
 * Refresh button labels per active tab.
 * @readonly
 * @type {Record<string, string>}
 */
export const REFRESH_LABEL_BY_TAB = Object.freeze({
    [PlanPlatformTab.JAVELINA]: "Refresh javelina list",
    [PlanPlatformTab.HRP]: "Refresh HealthEdge list",
});

/**
 * Toast messages for remaining stub actions.
 * @readonly
 * @type {Record<string, string>}
 */
export const PLANS_STUB_TOAST_MESSAGES = Object.freeze({
    EXPORT: "Plan export is not wired up yet.",
    IMPORT_CONFIRMED: "Import confirmed. Import is not wired up yet.",
});

/**
 * Success / failure copy shown in the refresh progress modal.
 * @readonly
 */
export const PLANS_REFRESH_TOAST_MESSAGES = Object.freeze({
    [PlanPlatformTab.JAVELINA]: "Javelina list refreshed.",
    [PlanPlatformTab.HRP]: "HealthEdge list refreshed.",
    FAILED: "Plan list refresh failed. Please try again.",
    START_FAILED: "Could not start plan list refresh. Please try again.",
    TIMEOUT: "Plan list refresh timed out. Please try again.",
});

/**
 * Shared `react-toastify` options for stub action toasts on this screen.
 * @type {import("react-toastify").ToastOptions}
 */
const PLANS_TOAST_OPTIONS = Object.freeze({
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
    transition: Bounce,
});

/** @readonly */
export const REFRESH_POLL_INTERVAL_MS = 1500;

/** @readonly */
export const REFRESH_TIMEOUT_MS = 10 * 60 * 1000;

/** @readonly */
export const REFRESH_RESULT_AUTO_CLOSE_MS = 3000;

/** @readonly */
export const REFRESH_JOB_STATUS = Object.freeze({
    RUNNING: "RUNNING",
    SUCCEEDED: "SUCCEEDED",
    FAILED: "FAILED",
});

/** @readonly */
export const REFRESH_OUTCOME_STATUS = Object.freeze({
    SUCCESS: "success",
    ERROR: "error",
});

/**
 * Tab option metadata for the platform toggle.
 * @readonly
 * @type {ReadonlyArray<{ value: string, label: string }>}
 */
export const PLATFORM_TAB_OPTIONS = Object.freeze([
    { value: PlanPlatformTab.JAVELINA, label: "Javelina Benefit Plans" },
    { value: PlanPlatformTab.HRP, label: "HealthEdge Benefit Plans" },
]);

/**
 * Drill-down route paths reached from Start/Edit actions on the Plans list.
 * @readonly
 * @type {{ PLAN_SETUP: string, PLAN_NOTIFICATION: string }}
 */
export const PLAN_MANAGER_ROUTE_PATHS = Object.freeze({
    PLAN_SETUP: "/plan-manager/plans/setup",
    PLAN_NOTIFICATION: "/plan-manager/plans/notification",
});

const PLANS_TABLE_PANEL_ID = "plans-table-panel";
const PLANS_LOAD_ERROR_MESSAGE =
    "Plan manager data is currently unavailable. Please try again later.";

export const PLANS_TAB_OPTIONS = PLATFORM_TAB_OPTIONS.map((option) => ({
    ...option,
    controls: PLANS_TABLE_PANEL_ID,
}));

const downloadJsonFile = (fileName, data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
    });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
};

const waitMs = (ms) =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

const EMPTY_REFRESH_PROGRESS = Object.freeze({
    percent: 0,
    stage: "",
    message: "",
});

export const readPlanFileAsText = async (file) => {
    if (!file) {
        return "";
    }
    if (typeof file.text === "function") {
        return await file.text();
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () =>
            reject(reader.error ?? new Error("Failed to read file"));
        reader.readAsText(file);
    });
};

/**
 * Ephemeral UI state and handlers for the plans list screen.
 * @returns {object} Values and callbacks for the presentational view.
 */
export function usePlans() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const setSelectedPlan = useSelectedPlanStore((state) => state.setSelectedPlan);

    const [activeTab, setActiveTab] = useState(PlanPlatformTab.JAVELINA);
    const [searchCriteria, setSearchCriteria] = useState("");
    const [selectedClientName, setSelectedClientName] = useState(null);
    const [isImportOverwriteModalOpen, setIsImportOverwriteModalOpen] =
        useState(false);
    const [isPlanIdMismatchModalOpen, setIsPlanIdMismatchModalOpen] =
        useState(false);
    const [planIdMismatchDetails, setPlanIdMismatchDetails] = useState(null);
    const [pendingImportData, setPendingImportData] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isRefreshInProgress, setIsRefreshInProgress] = useState(false);
    const [refreshProgress, setRefreshProgress] = useState(EMPTY_REFRESH_PROGRESS);
    const [refreshOutcome, setRefreshOutcome] = useState(null);

    const importFileInputRef = useRef(null);
    const refreshAutoCloseTimerRef = useRef(null);

    const clearRefreshAutoCloseTimer = () => {
        if (refreshAutoCloseTimerRef.current != null) {
            clearTimeout(refreshAutoCloseTimerRef.current);
            refreshAutoCloseTimerRef.current = null;
        }
    };

    const handleCloseRefreshModal = () => {
        clearRefreshAutoCloseTimer();
        setIsRefreshInProgress(false);
        setRefreshProgress(EMPTY_REFRESH_PROGRESS);
        setRefreshOutcome(null);
    };

    const presentRefreshOutcome = (status, message) => {
        setRefreshOutcome({ status, message });
        clearRefreshAutoCloseTimer();
        refreshAutoCloseTimerRef.current = setTimeout(() => {
            refreshAutoCloseTimerRef.current = null;
            setIsRefreshInProgress(false);
            setRefreshProgress(EMPTY_REFRESH_PROGRESS);
            setRefreshOutcome(null);
        }, REFRESH_RESULT_AUTO_CLOSE_MS);
    };

    useEffect(
        () => () => {
            clearRefreshAutoCloseTimer();
        },
        [],
    );

    const plansAllQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.plansAll,
        queryFn: () =>
            loadAllPlansGrids({
                fetchJavelinaConfigPage: fetchPlanManagerClientConfig,
                fetchJavelinaBatch: fetchPlanManagerPlansBatch,
                fetchHealthEdgeConfigPage: fetchPlanManagerHealthEdgeConfig,
                fetchHealthEdgeBatch: fetchPlanManagerHealthEdgePlansBatch,
            }),
        ...PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS,
    });

    useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.defaultNotificationConfig,
        queryFn: fetchPlanManagerDefaultNotificationConfig,
        ...PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS,
    });

    const jvPlans = plansAllQuery.data?.jvPlans ?? [];
    const hrpPlans = plansAllQuery.data?.hrpPlans ?? [];

    const tabPlans = useMemo(
        () => (activeTab === PlanPlatformTab.HRP ? hrpPlans : jvPlans),
        [activeTab, hrpPlans, jvPlans],
    );

    const visiblePlans = useMemo(
        () =>
            filterPlansBySearchCriteria(
                tabPlans,
                searchCriteria,
                activeTab === PlanPlatformTab.HRP,
            ),
        [tabPlans, searchCriteria, activeTab],
    );

    const isPlansLoading = plansAllQuery.isLoading;
    const isPlansError = plansAllQuery.isError;
    const plansErrorMessage =
        plansAllQuery.error?.message ?? PLANS_LOAD_ERROR_MESSAGE;

    const handleTabChange = (tab) => {
        if (isRefreshInProgress) {
            return;
        }
        setActiveTab(tab);
        setSelectedClientName(null);
    };

    const handleSearchCriteriaChange = (value) => {
        if (isRefreshInProgress) {
            return;
        }
        setSearchCriteria(value);
    };

    const handleRefresh = async () => {
        if (isRefreshInProgress) {
            return;
        }

        clearRefreshAutoCloseTimer();
        setRefreshOutcome(null);
        setIsRefreshInProgress(true);
        setRefreshProgress({
            percent: 0,
            stage: "starting",
            message: "Starting refresh…",
        });

        const completeRefresh = async () => {
            await queryClient.invalidateQueries({
                queryKey: PLAN_MANAGER_QUERY_KEYS.plansAll,
            });
            await queryClient.refetchQueries({
                queryKey: PLAN_MANAGER_QUERY_KEYS.plansAll,
            });
            setRefreshProgress((previous) => ({
                ...previous,
                percent: 100,
            }));
            presentRefreshOutcome(
                REFRESH_OUTCOME_STATUS.SUCCESS,
                PLANS_REFRESH_TOAST_MESSAGES[activeTab],
            );
        };

        try {
            const startResult =
                activeTab === PlanPlatformTab.HRP
                    ? await startPlanManagerHealthEdgeRefresh()
                    : await startPlanManagerJavelinaRefresh();

            if (startResult == null) {
                presentRefreshOutcome(
                    REFRESH_OUTCOME_STATUS.ERROR,
                    PLANS_REFRESH_TOAST_MESSAGES.START_FAILED,
                );
                return;
            }

            // The plan-management sync endpoints currently run synchronously and
            // return a plain success body; only async job starts carry a jobId.
            const jobId = startResult?.jobId;
            if (!jobId) {
                await completeRefresh();
                return;
            }

            const startedAt = Date.now();
            while (Date.now() - startedAt < REFRESH_TIMEOUT_MS) {
                const status = await fetchPlanManagerRefreshJobStatus(jobId);
                if (!status) {
                    presentRefreshOutcome(
                        REFRESH_OUTCOME_STATUS.ERROR,
                        PLANS_REFRESH_TOAST_MESSAGES.FAILED,
                    );
                    return;
                }

                setRefreshProgress({
                    percent: status.percent ?? 0,
                    stage: status.stage ?? "",
                    message:
                        status.message ||
                        status.stage ||
                        "Refreshing plan list…",
                });

                if (status.status === REFRESH_JOB_STATUS.SUCCEEDED) {
                    await completeRefresh();
                    return;
                }

                if (status.status === REFRESH_JOB_STATUS.FAILED) {
                    presentRefreshOutcome(
                        REFRESH_OUTCOME_STATUS.ERROR,
                        status.message || PLANS_REFRESH_TOAST_MESSAGES.FAILED,
                    );
                    return;
                }

                await waitMs(REFRESH_POLL_INTERVAL_MS);
            }

            presentRefreshOutcome(
                REFRESH_OUTCOME_STATUS.ERROR,
                PLANS_REFRESH_TOAST_MESSAGES.TIMEOUT,
            );
        } catch {
            presentRefreshOutcome(
                REFRESH_OUTCOME_STATUS.ERROR,
                PLANS_REFRESH_TOAST_MESSAGES.FAILED,
            );
        }
    };

    const handleStartOrEditPlan = (row) => {
        setSelectedPlan(row, activeTab);
        setSelectedClientName(row.clientName);
        const targetUrl = buildPlanDrillDownUrl(
            PLAN_MANAGER_ROUTE_PATHS.PLAN_SETUP,
            {
                clientId: row.clientId,
                planId: row.planId,
                platformTab: activeTab,
            },
        );
        navigate(targetUrl);
    };

    const handleStartOrEditNotification = (row) => {
        setSelectedPlan(row, activeTab);
        setSelectedClientName(row.clientName);
        const targetUrl = buildPlanDrillDownUrl(
            PLAN_MANAGER_ROUTE_PATHS.PLAN_NOTIFICATION,
            {
                clientId: row.clientId,
                planId: row.planId,
                platformTab: activeTab,
            },
        );
        navigate(targetUrl);
    };

    const handleExportPlan = async (row) => {
        if (!row?.clientId || !row?.planId) {
            return;
        }

        try {
            const exportPayload =
                activeTab === PlanPlatformTab.HRP
                    ? await fetchPlanManagerHealthEdgeMedicalPlan(
                          row.clientId,
                          row.planId,
                      )
                    : await fetchPlanManagerMedicalPlan(row.clientId, row.planId);

            if (!exportPayload) {
                throw new Error(PLANS_LOAD_ERROR_MESSAGE);
            }

            const sanitized = sanitizePlanExportPayload(exportPayload);
            const fileName = buildPlanExportFileName({
                clientName: row.clientName,
                planId: row.planId,
            });

            downloadJsonFile(fileName, sanitized);
        } catch {
            toast.error(PLANS_LOAD_ERROR_MESSAGE, PLANS_TOAST_OPTIONS);
        }
    };

    const handleImportPlan = (row) => {
        setSelectedRow(row);
        if (row.status === PlanStatus.NOT_STARTED) {
            importFileInputRef.current?.click();
            return;
        }
        setIsImportOverwriteModalOpen(true);
    };

    const completeSuccessfulImport = (fileData) => {
        if (selectedRow) {
            setSelectedPlan(selectedRow, activeTab, {
                importedPlanData: fileData,
                isReview: true,
            });
            setSelectedClientName(selectedRow.clientName);
            const targetUrl = buildPlanDrillDownUrl(
                PLAN_MANAGER_ROUTE_PATHS.PLAN_SETUP,
                {
                    clientId: selectedRow.clientId,
                    planId: selectedRow.planId,
                    platformTab: activeTab,
                },
            );
            navigate(targetUrl);
        }
    };

    const processImportFile = async (chosenFile) => {
        if (!chosenFile) {
            return;
        }

        const nameValidation = validatePlanImportFileName(chosenFile.name);
        if (!nameValidation.isValid) {
            toast.error(nameValidation.error, PLANS_TOAST_OPTIONS);
            return;
        }

        try {
            const rawText = await readPlanFileAsText(chosenFile);
            const parseResult = parsePlanImportJson(rawText);
            if (!parseResult.isValid) {
                toast.error(parseResult.error, PLANS_TOAST_OPTIONS);
                return;
            }

            const isHrp = activeTab === PlanPlatformTab.HRP;
            const idValidation = validatePlanImportIds({
                fileData: parseResult.data,
                targetRow: selectedRow,
                isHrp,
            });

            if (!idValidation.canImport) {
                toast.error(idValidation.error, PLANS_TOAST_OPTIONS);
                return;
            }

            if (idValidation.requiresConfirmation) {
                setPendingImportData(parseResult.data);
                setPlanIdMismatchDetails({
                    title: IMPORT_MODAL_COPY.PLAN_ID_MISMATCH.TITLE,
                    description: `You are about to import plan configuration from "${idValidation.importedPlanId}" to "${idValidation.targetPlanId}".`,
                });
                setIsPlanIdMismatchModalOpen(true);
                return;
            }

            completeSuccessfulImport(parseResult.data);
        } catch {
            toast.error(
                PLAN_IMPORT_ERROR_MESSAGES.INVALID_JSON,
                PLANS_TOAST_OPTIONS,
            );
        }
    };

    const handleImportFileSelected = (event) => {
        const chosenFile = event.target.files?.[0];
        event.target.value = "";
        if (!chosenFile) {
            return undefined;
        }

        return processImportFile(chosenFile);
    };

    const handleOverwriteFileSelected = (event) => {
        const chosenFile = event.target.files?.[0];
        event.target.value = "";
        if (!chosenFile) {
            return undefined;
        }

        setIsImportOverwriteModalOpen(false);
        return processImportFile(chosenFile);
    };

    const handleOverwriteCancel = () => {
        setIsImportOverwriteModalOpen(false);
        setSelectedRow(null);
    };

    const handlePlanIdMismatchConfirm = () => {
        setIsPlanIdMismatchModalOpen(false);
        if (pendingImportData) {
            const dataToImport = pendingImportData;
            setPendingImportData(null);
            setPlanIdMismatchDetails(null);
            completeSuccessfulImport(dataToImport);
        }
    };

    const handlePlanIdMismatchCancel = () => {
        setIsPlanIdMismatchModalOpen(false);
        setPendingImportData(null);
        setPlanIdMismatchDetails(null);
        setSelectedRow(null);
    };

    const handleSelectClient = (clientId) => {
        const matchingRow = tabPlans.find((plan) => plan.clientId === clientId);
        setSelectedClientName(matchingRow ? matchingRow.clientName : null);
    };

    return {
        activeTab,
        searchCriteria,
        setSearchCriteria: handleSearchCriteriaChange,
        selectedClientName,
        isImportOverwriteModalOpen,
        isPlanIdMismatchModalOpen,
        planIdMismatchDetails,
        importFileInputRef,
        visiblePlans,
        isPlansLoading,
        isPlansError,
        plansErrorMessage,
        isRefreshInProgress,
        refreshProgress,
        refreshOutcome,
        handleCloseRefreshModal,
        handleTabChange,
        handleRefresh,
        handleStartOrEditPlan,
        handleStartOrEditNotification,
        handleExportPlan,
        handleImportPlan,
        handleImportFileSelected,
        handleOverwriteFileSelected,
        handleOverwriteCancel,
        handlePlanIdMismatchConfirm,
        handlePlanIdMismatchCancel,
        handleSelectClient,
    };
}
